import { type Vec, distToSegment } from './vec';

/** Ramer–Douglas–Peucker simplification of an open polyline (iterative, no recursion depth issues). */
export function simplifyPolyline(points: Vec[], tol: number): Vec[] {
  const n = points.length;
  if (n <= 2 || tol <= 0) return points.slice();
  const keep = new Uint8Array(n);
  keep[0] = 1;
  keep[n - 1] = 1;
  const stack: [number, number][] = [[0, n - 1]];
  while (stack.length) {
    const [s, e] = stack.pop()!;
    let maxD = -1;
    let idx = -1;
    for (let i = s + 1; i < e; i++) {
      const d = distToSegment(points[i], points[s], points[e]);
      if (d > maxD) {
        maxD = d;
        idx = i;
      }
    }
    if (idx >= 0 && maxD > tol) {
      keep[idx] = 1;
      stack.push([s, idx], [idx, e]);
    }
  }
  const out: Vec[] = [];
  for (let i = 0; i < n; i++) if (keep[i]) out.push(points[i]);
  return out;
}

/**
 * Simplifies a closed ring. The ring is split at the vertex farthest from vertex 0 so that the
 * two anchor points are stable, then each half is simplified.
 */
export function simplifyRing(ring: Vec[], tol: number): Vec[] {
  const n = ring.length;
  if (n <= 4 || tol <= 0) return ring.slice();
  let far = 0;
  let farD = -1;
  for (let i = 1; i < n; i++) {
    const d = (ring[i].x - ring[0].x) ** 2 + (ring[i].y - ring[0].y) ** 2;
    if (d > farD) {
      farD = d;
      far = i;
    }
  }
  const a = simplifyPolyline(ring.slice(0, far + 1), tol);
  const b = simplifyPolyline([...ring.slice(far), ring[0]], tol);
  const out = [...a, ...b.slice(1, -1)];
  return out.length >= 3 ? out : ring.slice();
}

/** Chaikin corner cutting on a closed ring; keeps corners sharper than `keepAngleDeg` intact. */
export function smoothRing(ring: Vec[], iterations = 1, keepAngleDeg = 60): Vec[] {
  let cur = ring;
  const cosLimit = Math.cos((keepAngleDeg * Math.PI) / 180);
  for (let it = 0; it < iterations; it++) {
    const n = cur.length;
    if (n < 4) return cur;
    const out: Vec[] = [];
    for (let i = 0; i < n; i++) {
      const prev = cur[(i - 1 + n) % n];
      const p = cur[i];
      const next = cur[(i + 1) % n];
      const ax = p.x - prev.x, ay = p.y - prev.y;
      const bx = next.x - p.x, by = next.y - p.y;
      const la = Math.hypot(ax, ay), lb = Math.hypot(bx, by);
      const cos = la > 0 && lb > 0 ? (ax * bx + ay * by) / (la * lb) : 1;
      if (cos < cosLimit) {
        // sharp corner: keep it
        out.push(p);
      } else {
        out.push({ x: 0.75 * p.x + 0.25 * prev.x, y: 0.75 * p.y + 0.25 * prev.y });
        out.push({ x: 0.75 * p.x + 0.25 * next.x, y: 0.75 * p.y + 0.25 * next.y });
      }
    }
    cur = out;
  }
  return cur;
}
